import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "modal-header" }
const _hoisted_2 = { class: "modal-body" }
const _hoisted_3 = {
  key: 0,
  class: "tabs-container"
}
const _hoisted_4 = { class: "form-group" }
const _hoisted_5 = ["value"]
const _hoisted_6 = {
  key: 1,
  class: "form-group"
}
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = {
  key: 2,
  class: "form-group"
}
const _hoisted_9 = { class: "form-row" }
const _hoisted_10 = { class: "form-group" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "form-group" }
const _hoisted_13 = ["value"]
const _hoisted_14 = {
  key: 3,
  class: "form-group"
}
const _hoisted_15 = {
  key: 4,
  class: "form-group"
}
const _hoisted_16 = ["value", "min"]
const _hoisted_17 = {
  key: 0,
  class: "repeat-count-indication"
}
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = {
  key: 5,
  class: "form-group checkbox-group"
}
const _hoisted_20 = {
  key: 0,
  class: "tentative-3state"
}
const _hoisted_21 = { class: "checkbox-label" }
const _hoisted_22 = ["checked", "indeterminate"]
const _hoisted_23 = { class: "tentative-label" }
const _hoisted_24 = {
  key: 1,
  class: "checkbox-label"
}
const _hoisted_25 = ["checked"]
const _hoisted_26 = { class: "form-group" }
const _hoisted_27 = ["value"]
const _hoisted_28 = { class: "modal-footer" }

import { computed, ref, toRefs, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import {
  PlannedTimeFrequency,
  LeaveType,
} from "@/helpers/project/typesDefinition";
import { calculateRepeatCount } from "@/helpers/planning/planningRecurrence";
import { formatMinutesAsTime, parseTimeToMinutes } from "@/helpers/utils";
import {
  GET_BASIC_EMPLOYEE_PROJECTS,
  GET_EMPLOYEE_ROLES,
} from "@/graphql/project/queries";
import type {
  PlanningEditMode,
  PlanningFormData,
  PlanningItemType,
  PlanningProject,
  PlanningUser,
} from "./type";

interface PlanningRoleOption {
  id: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanningModal',
  props: {
    formData: {},
    isEditing: { type: Boolean },
    users: {},
    projects: {},
    currentProject: {},
    mode: {},
    editMode: {},
    hasParent: { type: Boolean },
    occurrenceTentative: { type: Boolean }
  },
  emits: ["update:formData", "update:mode", "update:editMode", "close", "save", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const {
  formData,
  isEditing,
  users,
  projects,
  currentProject,
  mode,
  editMode,
  hasParent,
} = toRefs(props);

const emit = __emit;

const currentMode = computed<PlanningItemType>(() => {
  return mode.value ?? "planned_time";
});

const modalTitle = computed(() => {
  if (currentMode.value === "time_off") {
    return isEditing.value ? "Edit Time Off" : "New Time Off";
  }

  return isEditing.value ? "Edit Planned Time" : "New Planned Time";
});

// Fetch projects for the selected user (only projects where user has an active role)
const selectedUserId = computed(() => formData.value.userId);

const basicProjectQueryVariables = computed(() => ({
  userId: selectedUserId.value || undefined,
}));

const { result: basicEmployeeProjectsResult } = useQuery(
  GET_BASIC_EMPLOYEE_PROJECTS,
  basicProjectQueryVariables,
  () => ({
    enabled: !!selectedUserId.value && currentMode.value === "planned_time",
  }),
);

const availableProjects = computed<PlanningProject[]>(() => {
  let projectList: PlanningProject[];

  // If we have projects for the selected user, use those
  if (basicEmployeeProjectsResult.value?.basicEmployeeProjects) {
    projectList = basicEmployeeProjectsResult.value.basicEmployeeProjects;
  } else {
    // Otherwise, fall back to all projects (e.g., when editing and no user selected yet)
    projectList = projects.value;
  }

  // When editing, make sure the currently assigned project is present in the
  // options (it may be archived or the user may no longer have an active role),
  // so the readonly dropdown can still display it.
  if (isEditing.value && formData.value.projectId && currentProject.value) {
    const alreadyInList = projectList.some(
      (project) => project.id === currentProject.value!.id,
    );
    if (!alreadyInList) {
      // Create a plain copy to avoid issues with frozen Apollo objects
      projectList = [...projectList, { ...currentProject.value }];
    }
  }

  return projectList;
});

// Fetch active project roles for the selected employee + project.
// The backend only returns roles where the user has an active role relation
// and the role itself is active.
const employeeRolesQueryVariables = computed(() => ({
  projectId: formData.value.projectId || undefined,
  userId: formData.value.userId || undefined,
}));

const { result: employeeRolesResult } = useQuery(
  GET_EMPLOYEE_ROLES,
  employeeRolesQueryVariables,
  () => ({
    enabled:
      !!formData.value.projectId &&
      !!formData.value.userId &&
      currentMode.value === "planned_time",
  }),
);

const EMPTY_ROLES: PlanningRoleOption[] = [];

// Map to fresh plain objects so Multiselect can safely normalize them
// (Apollo results are frozen and Multiselect mutates option labels).
const availableRoles = computed<PlanningRoleOption[]>(() => {
  const roles = employeeRolesResult.value?.employeeRoles;
  if (!roles || roles.length === 0) {
    return EMPTY_ROLES;
  }
  return roles.map((role) => ({ id: role.id, name: role.name }));
});

// Preselect the first non-"Offered" active role for the employee when creating,
// keep the current role when it is still valid (e.g. when editing).
// The watcher only emits when the role actually needs to change, which avoids
// infinite update loops while the role query is loading.
watch(
  () => [formData.value.userId, formData.value.projectId, availableRoles.value],
  () => {
    if (currentMode.value !== "planned_time") {
      return;
    }

    const roles = availableRoles.value;
    const currentRoleId = formData.value.projectRoleId;

    // Keep the current selection while it remains a valid option
    if (currentRoleId && roles.some((role) => role.id === currentRoleId)) {
      return;
    }

    // Roles are not loaded yet (or none available): don't touch the selection
    if (roles.length === 0) {
      return;
    }

    const nonOffered = roles.filter(
      (role) => role.name.trim().toLowerCase() !== "offered",
    );
    const preferredRole = nonOffered[0] ?? roles[0];
    const newRoleId = preferredRole?.id ?? null;

    if (newRoleId !== currentRoleId) {
      updateField("projectRoleId", newRoleId);
    }
  },
  { immediate: true },
);

const frequencyOptions = [
  {
    label: "Weekly",
    value: PlannedTimeFrequency.WEEKLY,
  },
  {
    label: "Monthly",
    value: PlannedTimeFrequency.MONTHLY,
  },
  {
    label: "Yearly",
    value: PlannedTimeFrequency.YEARLY,
  },
  {
    label: "Every 2 weeks",
    value: PlannedTimeFrequency.EVERY_2_WEEKS,
  },
  {
    label: "Every 3 weeks",
    value: PlannedTimeFrequency.EVERY_3_WEEKS,
  },
  {
    label: "Every 6 weeks",
    value: PlannedTimeFrequency.EVERY_6_WEEKS,
  },
  {
    label: "Every 3 months",
    value: PlannedTimeFrequency.EVERY_3_MONTHS,
  },
  {
    label: "Every 6 months",
    value: PlannedTimeFrequency.EVERY_6_MONTHS,
  },
];

const typeOptions = [
  { value: "planned_time" as PlanningItemType, label: "Planned Time" },
  { value: "time_off" as PlanningItemType, label: "Time Off" },
];

const usersWithName = computed(() => {
  return users.value.map((user) => ({
    ...user,
    name: getUserName(user),
  }));
});

const allFrequencyOptions = [
  { label: "No recurrence", value: null },
  ...frequencyOptions,
];

const leaveTypeOptions = [
  { label: "Paid Leave", value: "paid_leave" },
  { label: "Sick Leave", value: "sick_leave" },
];

const updateField = <K extends keyof PlanningFormData>(
  key: K,
  value: PlanningFormData[K],
) => {
  emit("update:formData", {
    ...formData.value,
    [key]: value,
  });
};

const handleModeMultiselectChange = (newMode: PlanningItemType) => {
  emit("update:mode", newMode);

  emit("update:formData", {
    ...formData.value,
    projectId: newMode === "time_off" ? null : formData.value.projectId,
    type:
      newMode === "time_off"
        ? formData.value.type ?? ("paid_leave" as LeaveType)
        : formData.value.type,
  });
};

const getUserName = (user: PlanningUser) => {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "Unknown";
};

const getInputValue = (event: Event) => {
  return (event.target as HTMLInputElement).value;
};

const getTextareaValue = (event: Event) => {
  return (event.target as HTMLTextAreaElement).value;
};

const durationPerDayInput = ref("");

watch(
  () => formData.value.durationPerDay,
  (durationPerDay) => {
    durationPerDayInput.value = formatMinutesAsTime(durationPerDay ?? 480);
  },
  { immediate: true },
);

// Reset projectId when user changes and current project is not in available projects
watch(
  () => formData.value.userId,
  () => {
    const currentProjectId = formData.value.projectId;
    if (currentProjectId && availableProjects.value.length > 0) {
      const projectStillAvailable = availableProjects.value.some(
        (p) => p.id === currentProjectId,
      );
      if (!projectStillAvailable) {
        updateField("projectId", null);
      }
    }
  },
);

const onDurationPerDayBlur = () => {
  const minutes = parseTimeToMinutes(durationPerDayInput.value);
  const safeMinutes = minutes > 0 ? minutes : 480;

  updateField("durationPerDay", safeMinutes);
  durationPerDayInput.value = formatMinutesAsTime(safeMinutes);
};

const getOptionalDateValue = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;

  return value || null;
};

// Handle 3-state tentative checkbox change for "all occurrences" mode
// Cycle through: null (don't update) -> true (checked) -> false (unchecked) -> null
const handleTentative3StateChange = (event: Event) => {
  const currentValue = formData.value.tentative;
  const checkbox = event.target as HTMLInputElement;

  // If current is null or undefined (default), set to true (checked)
  // If current is true, set to false (unchecked)
  // If current is false, set to null (don't update)
  let newValue: boolean | null;

  if (currentValue === null || currentValue === undefined) {
    newValue = true;
  } else if (currentValue === true) {
    newValue = false;
  } else {
    newValue = null;
  }

  // Update checkbox visual state
  checkbox.checked = newValue === true;
  checkbox.indeterminate = newValue === null;

  updateField("tentative", newValue);
};

// Get label for 3-state tentative checkbox
const getTentative3StateLabel = () => {
  const value = formData.value.tentative;
  if (value === null || value === undefined) {
    return "Don't update tentative";
  }
  return value ? "Tentative (all)" : "Not tentative (all)";
};

const repeatCount = computed(() => {
  return calculateRepeatCount(
    formData.value.startDate,
    formData.value.repeatEndDate,
    formData.value.frequency,
  );
});

// Watch editMode to initialize tentative appropriately:
// - When switching to "all" mode: set tentative to null (don't update)
// - When switching to "single" mode: set to the occurrence's tentative value
watch(
  () => editMode.value,
  (newMode, oldMode) => {
    if (
      newMode !== oldMode &&
      isEditing.value &&
      currentMode.value === "planned_time"
    ) {
      if (newMode === "all") {
        // Switch to "all" mode - set to null (don't update tentative)
        updateField("tentative", null);
      } else if (newMode === "single" && oldMode === "all") {
        // Switch to "single" mode - use the occurrence's tentative value
        // This is passed as a prop from the parent component
        updateField("tentative", props.occurrenceTentative ?? false);
      }
    }
  },
);

// Initialize tentative to null when opening edit modal in "all" mode
// This runs when the modal is opened with editMode === "all"
watch(
  [() => isEditing.value, () => editMode.value, () => formData.value.startDate],
  ([newIsEditing, newEditMode]) => {
    // Only set to null if we're editing, in "all" mode, and the form is initialized
    if (
      newIsEditing &&
      newEditMode === "all" &&
      currentMode.value === "planned_time" &&
      formData.value.startDate
    ) {
      // Default to null (don't update) when editing all occurrences
      // This ensures the 3-state checkbox shows "-" by default
      updateField("tentative", null);
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_Multiselect = _resolveComponent("Multiselect")!

  return (_openBlock(), _createElementBlock("div", {
    class: "modal-overlay",
    onClick: _cache[18] || (_cache[18] = ($event: any) => (_ctx.$emit('close')))
  }, [
    _createElementVNode("div", {
      class: "modal-content",
      onClick: _cache[17] || (_cache[17] = _withModifiers(() => {}, ["stop"]))
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", null, _toDisplayString(modalTitle.value), 1),
        _createElementVNode("button", {
          class: "close-btn",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
        }, "×")
      ]),
      _createElementVNode("div", _hoisted_2, [
        (_unref(isEditing) && _unref(hasParent))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["tab-btn", { active: _unref(editMode) === 'all' }]),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('update:editMode', 'all')))
              }, " All Occurrences ", 2),
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["tab-btn", { active: _unref(editMode) === 'single' }]),
                onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('update:editMode', 'single')))
              }, " This Occurrence Only ", 2)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          _cache[19] || (_cache[19] = _createElementVNode("label", null, "Type", -1)),
          (!_unref(isEditing))
            ? (_openBlock(), _createBlock(_component_Multiselect, {
                key: 0,
                value: currentMode.value,
                options: typeOptions,
                searchable: true,
                closeOnSelect: true,
                canClear: false,
                valueProp: "value",
                label: "label",
                placeholder: "Select Type",
                class: "planning-multiselect",
                onChange: handleModeMultiselectChange
              }, null, 8, ["value"]))
            : (_openBlock(), _createElementBlock("input", {
                key: 1,
                type: "text",
                class: "form-input",
                value: 
              currentMode.value === 'planned_time' ? 'Planned Time' : 'Time Off'
            ,
                readonly: "",
                disabled: ""
              }, null, 8, _hoisted_5))
        ]),
        (currentMode.value === 'planned_time')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[20] || (_cache[20] = _createElementVNode("label", null, "Project", -1)),
              _createVNode(_component_Multiselect, {
                value: _unref(formData).projectId,
                options: availableProjects.value,
                searchable: true,
                closeOnSelect: true,
                canClear: !_unref(isEditing),
                disabled: _unref(isEditing),
                valueProp: "id",
                label: "name",
                placeholder: "Select Project",
                class: "planning-multiselect",
                onChange: _cache[3] || (_cache[3] = (val) => updateField('projectId', val))
              }, null, 8, ["value", "options", "canClear", "disabled"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_7, [
          _cache[21] || (_cache[21] = _createElementVNode("label", null, "Employee", -1)),
          _createVNode(_component_Multiselect, {
            value: _unref(formData).userId,
            options: usersWithName.value,
            searchable: true,
            closeOnSelect: true,
            canClear: true,
            valueProp: "id",
            label: "name",
            placeholder: "Select Employee",
            class: "planning-multiselect",
            onChange: _cache[4] || (_cache[4] = (val) => updateField('userId', val))
          }, null, 8, ["value", "options"])
        ]),
        (currentMode.value === 'planned_time')
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _cache[22] || (_cache[22] = _createElementVNode("label", null, "Role", -1)),
              _createVNode(_component_Multiselect, {
                value: _unref(formData).projectRoleId,
                options: availableRoles.value,
                searchable: true,
                closeOnSelect: true,
                canClear: true,
                disabled: availableRoles.value.length === 1,
                valueProp: "id",
                label: "name",
                placeholder: "Select Role",
                class: "planning-multiselect",
                onChange: _cache[5] || (_cache[5] = (val) => updateField('projectRoleId', val))
              }, null, 8, ["value", "options", "disabled"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _cache[23] || (_cache[23] = _createElementVNode("label", null, "Start Date", -1)),
            _createElementVNode("input", {
              type: "date",
              value: _unref(formData).startDate,
              class: "form-input",
              onInput: _cache[6] || (_cache[6] = ($event: any) => (updateField('startDate', getInputValue($event))))
            }, null, 40, _hoisted_11)
          ]),
          _createElementVNode("div", _hoisted_12, [
            _cache[24] || (_cache[24] = _createElementVNode("label", null, "End Date", -1)),
            _createElementVNode("input", {
              type: "date",
              value: _unref(formData).endDate,
              class: "form-input",
              onInput: _cache[7] || (_cache[7] = ($event: any) => (updateField('endDate', getInputValue($event))))
            }, null, 40, _hoisted_13)
          ])
        ]),
        (!_unref(isEditing) || _unref(editMode) !== 'single')
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _cache[25] || (_cache[25] = _createElementVNode("label", null, "Frequency", -1)),
              _createVNode(_component_Multiselect, {
                value: _unref(formData).frequency,
                options: allFrequencyOptions,
                searchable: true,
                closeOnSelect: true,
                canClear: false,
                valueProp: "value",
                label: "label",
                placeholder: "No recurrence",
                class: "planning-multiselect",
                onChange: _cache[8] || (_cache[8] = (val) => updateField('frequency', val))
              }, null, 8, ["value"])
            ]))
          : _createCommentVNode("", true),
        (
            (!_unref(isEditing) || _unref(editMode) !== 'single') &&
            _unref(formData).frequency &&
            _unref(formData).frequency !== 'no_repeat'
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _cache[26] || (_cache[26] = _createElementVNode("label", null, "Until", -1)),
              _createElementVNode("input", {
                type: "date",
                value: _unref(formData).repeatEndDate ?? '',
                class: "form-input",
                min: _unref(formData).startDate,
                onInput: _cache[9] || (_cache[9] = ($event: any) => (updateField('repeatEndDate', getOptionalDateValue($event))))
              }, null, 40, _hoisted_16),
              (repeatCount.value > 0)
                ? (_openBlock(), _createElementBlock("span", _hoisted_17, " (" + _toDisplayString(repeatCount.value) + " times) ", 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_18, [
          _cache[27] || (_cache[27] = _createElementVNode("label", null, "Hours per Day", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "text",
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((durationPerDayInput).value = $event)),
            class: "form-input",
            placeholder: "08:00",
            onBlur: onDurationPerDayBlur
          }, null, 544), [
            [_vModelText, durationPerDayInput.value]
          ])
        ]),
        (currentMode.value === 'planned_time' && _unref(isEditing))
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
              (_unref(editMode) === 'all')
                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    _createElementVNode("label", _hoisted_21, [
                      _createElementVNode("input", {
                        type: "checkbox",
                        checked: _unref(formData).tentative === true,
                        indeterminate: _unref(formData).tentative === null,
                        onChange: handleTentative3StateChange
                      }, null, 40, _hoisted_22),
                      _createElementVNode("span", _hoisted_23, _toDisplayString(getTentative3StateLabel()), 1)
                    ]),
                    _cache[28] || (_cache[28] = _createElementVNode("span", { class: "tentative-hint" }, "(affects all occurrences)", -1))
                  ]))
                : (_unref(editMode) === 'single')
                  ? (_openBlock(), _createElementBlock("label", _hoisted_24, [
                      _createElementVNode("input", {
                        type: "checkbox",
                        checked: _unref(formData).tentative === true,
                        onChange: _cache[11] || (_cache[11] = ($event: any) => (
                updateField(
                  'tentative',
                  ($event.target as HTMLInputElement).checked,
                )
              ))
                      }, null, 40, _hoisted_25),
                      _cache[29] || (_cache[29] = _createTextVNode(" Tentative ", -1))
                    ]))
                  : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_26, [
          _createElementVNode("label", null, _toDisplayString(currentMode.value === "time_off" ? "Time Off Type" : "Notes"), 1),
          (currentMode.value === 'time_off')
            ? (_openBlock(), _createBlock(_component_Multiselect, {
                key: 0,
                value: _unref(formData).type ?? 'paid_leave',
                options: leaveTypeOptions,
                searchable: true,
                closeOnSelect: true,
                canClear: false,
                valueProp: "value",
                label: "label",
                placeholder: "Select Type",
                class: "planning-multiselect",
                onChange: _cache[12] || (_cache[12] = (val) => updateField('type', val))
              }, null, 8, ["value"]))
            : (_openBlock(), _createElementBlock("textarea", {
                key: 1,
                value: _unref(formData).notes,
                class: "form-input form-textarea",
                rows: "3",
                onInput: _cache[13] || (_cache[13] = ($event: any) => (updateField('notes', getTextareaValue($event))))
              }, null, 40, _hoisted_27))
        ])
      ]),
      _createElementVNode("div", _hoisted_28, [
        (_unref(isEditing))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-danger",
              onClick: _cache[14] || (_cache[14] = ($event: any) => (_ctx.$emit('delete')))
            }, " Delete "))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "btn btn-secondary",
          onClick: _cache[15] || (_cache[15] = ($event: any) => (_ctx.$emit('close')))
        }, " Cancel "),
        _createElementVNode("button", {
          class: "btn btn-primary",
          onClick: _cache[16] || (_cache[16] = ($event: any) => (_ctx.$emit('save')))
        }, "Save")
      ])
    ])
  ]))
}
}

})